import { useAppStore } from "@hooks/useAppStore";
import type { BaseItemSchema } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import FillInTheBlanksTypeIn from "@memorang/ui/components/StudySession/SessionContent/item-types/FillInTheBlanksTypeIn";
import { useCallback, useMemo } from "react";
import FormativeTimeOverrunContainer from "../../../containers/FormativeTimeOverrunContainer";
import {
	getBlankValuesResponse,
	toPersistableBlankValues,
} from "../../../helpers/ai-feedback";
import {
	extractBlankFields,
	normalizeBlankStem,
} from "../../../helpers/blank-tokens";
import {
	getAllowAudioReplay,
	getSummativeResponseConfig,
} from "../../../helpers/response-timing";
import { isTextAnswerMatch } from "../../../helpers/text-match";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import useResponseControlsStore from "../../../hooks/useResponseControlsStore";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

const EMPTY_BLANK_VALUES: Record<string, string> = {};

const FibTypeInContainer = ({
	item,
	isSplitLayout,
	isSummativeTest,
}: Props) => {
	const { content, response, scoring } = item;
	const itemId = item.id!;

	const { isAnswered } = useItemAnsweredState(item);

	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const answeredValues = useAIFeedbackStore((s) =>
		isAnswered
			? (getBlankValuesResponse(s.userResponse, itemId) ??
				item.answer?.selectedBlankValues ??
				EMPTY_BLANK_VALUES)
			: undefined,
	);

	const defaultValues = useMemo(
		() =>
			getBlankValuesResponse(
				useAIFeedbackStore.getState().userResponse,
				itemId,
			) ??
			item.answer?.selectedBlankValues ??
			EMPTY_BLANK_VALUES,
		[itemId, item.answer?.selectedBlankValues],
	);

	const rawStem = content.stem?.text ?? "";
	const stem = useMemo(() => normalizeBlankStem(rawStem), [rawStem]);
	const fields = useMemo(() => extractBlankFields(rawStem), [rawStem]);

	const handleChangeValues = useCallback(
		(nextValues: Record<string, string>) => {
			setUserResponse(itemId, toPersistableBlankValues(nextValues));
		},
		[itemId, setUserResponse],
	);

	const audioUri =
		content.stimulus?.type === "AUDIO"
			? (content.stimulus.media?.[0]?.url ?? "")
			: "";
	const instruction =
		response.type === "SHORT_TEXT_ENTRY" ? (response.instruction ?? "") : "";

	const correctValues = useMemo((): Record<string, string> | undefined => {
		if (!(scoring && "type" in scoring) || scoring.type !== "ANSWER_KEY_TEXT") {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("blankId" in entry) {
				map[entry.blankId] = entry.word;
			}
		}
		return map;
	}, [scoring]);
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest || showTimerConfig,
	);
	const prepTime = responseConfig?.prepTime;
	const allowAudioReplay = getAllowAudioReplay(responseConfig, isSummativeTest);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);
	const handleAudioEnded = useCallback(
		() => markAudioComplete(itemId),
		[markAudioComplete, itemId],
	);

	return (
		<Box gap={16}>
			<FillInTheBlanksTypeIn
				audioUri={audioUri}
				instruction={instruction}
				stem={stem}
				fields={fields}
				defaultValues={defaultValues}
				values={isAnswered ? answeredValues : undefined}
				onChangeValues={handleChangeValues}
				disabled={isAnswered}
				prepTime={prepTime}
				allowAudioReplay={allowAudioReplay}
				onAudioEnded={handleAudioEnded}
				correctValues={isAnswered ? correctValues : undefined}
				isValueCorrect={isTextAnswerMatch}
			/>
			<FormativeTimeOverrunContainer />
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default FibTypeInContainer;
